import { type ColDef, type ValueFormatterParams } from 'ag-grid-community'
import { itemStatusLabel } from '../../shared/ui/Badge'
import { procField, type TableRow } from './types'

/** Поля, скрытые от роли PROCUREMENT */
// У закупщика/РОЗ прячем менеджерские колонки: имя клиента (только код) и
// глобальный item.status (менеджерский цикл). Закупщик оперирует личным
// procurementState — его видно в очереди и карточке позиции.
export const PROCUREMENT_HIDDEN_FIELDS = new Set(['clientName', 'status'])

interface ProcUser {
  id: string
  email: string
  name: string | null
}

const PRIORITY_LABEL: Record<string, string> = {
  HIGH: '🔴 Высокий',
  MEDIUM: '🟡 Средний',
  LOW: '⚪ Базовый',
}

function formatPriority(p: ValueFormatterParams<TableRow>): string {
  const v = String(p.value ?? '')
  return PRIORITY_LABEL[v] ?? v
}

function formatStatus(p: ValueFormatterParams<TableRow>): string {
  const v = String(p.value ?? '')
  return v ? itemStatusLabel(v as Parameters<typeof itemStatusLabel>[0]) : ''
}

/** Строит список колонок: базовые + по одному слоту на каждого закупщика */
export function buildTableColDefs(procUsers: ProcUser[]): ColDef<TableRow>[] {
  const baseCols: ColDef<TableRow>[] = [
    { field: 'clientName', headerName: 'Клиент', width: 180 },
    { field: 'requestId', headerName: 'Заявка', width: 140 },
    { field: 'code', headerName: 'Код\n(Manager code)', width: 130 },
    {
      field: 'clientCategory',
      headerName: 'Категория клиента\n(Client category):\nA, B, C',
      width: 145,
    },
    {
      field: 'date',
      headerName: 'Дата (Date)',
      width: 130,
      cellEditor: 'agDateStringCellEditor',
      filterParams: { dateMode: true },
      valueFormatter: (p) => {
        if (!p.value) return ''
        const d = new Date(String(p.value))
        return d.toLocaleDateString('ru-RU')
      },
    },
    { field: 'partNumber', headerName: 'Part Number', width: 170 },
    { field: 'brand', headerName: 'Бренд (Brand)', width: 150 },
    { field: 'qty', headerName: 'Кол-во\n(QTY）', width: 100 },
    { field: 'unit', headerName: 'ед.', width: 65 },
    { field: 'totalY', headerName: 'Сумма\nстрочки\nY', width: 110 },
    { field: 'soprovod', headerName: 'Сопровод', width: 130 },
    {
      field: 'priority',
      headerName: 'Приоритет\nзакупки:\nВысокий\nСредний\nБазовый',
      width: 140,
      valueFormatter: formatPriority,
    },
    { field: 'deadline', headerName: 'Срок исполнения КП\n(Deadline CO)', width: 160 },
    { field: 'comments', headerName: 'Примечания\nсопровода (Comments)', width: 185 },
    { field: 'targetPrice', headerName: 'Target price', width: 115 },
    {
      field: 'status',
      headerName: 'Статус заявки\nActuality',
      width: 160,
      valueFormatter: formatStatus,
    },
  ]

  // По одной колонке на каждого существующего закупщика. Нет закупщиков —
  // нет столбцов (а не 8 пустых, как было).
  const slotCols: ColDef<TableRow>[] = procUsers.map((user) => ({
    field: procField(user.id) as `proc_${string}`,
    headerName: user.name || user.email,
    width: 130,
  }))

  return [...baseCols, ...slotCols]
}
